import { useEffect, useRef, useState } from 'react';
import {
  STATUS_POOLS,
  SETTINGS_ROLES,
  REMINDER_FORM_DOMAINS,
  CONTACT_TYPE_ALL,
  type StatusPool,
  type ContactStatusRow,
} from '@ehr/shared';
import { Dd, MultiDd, type DdOption } from '../components/Dropdown.js';
import { api } from '../lib/api.js';
import { askConfirm } from '../lib/dialogs.js';

/**
 * Status Actions manager (Settings → Status Actions), rebuilt 1:1 with V38. A
 * saved action is a labelled transition: fired from a contact's menu it advances
 * the per-pool ContactStatus ladder to the New Status, bumps a Global/Unique
 * counter, logs a touch, and optionally reminds the user to fill a form.
 *
 * The CONTACT TYPE selector chooses the pool; the pickers (Current/Show-on/New
 * status) list that pool's rungs, and the saved list is filtered to it. An
 * action's pool is derived from its target rung — the mockup's getActionType.
 * The Run button here is a PREVIEW (real firing lives on the contact menu).
 */

const cap = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const POOL_OPTIONS: DdOption[] = STATUS_POOLS.map((p) => ({ value: p.key, label: p.label }));
const poolLabel = (p: StatusPool) => STATUS_POOLS.find((x) => x.key === p)?.label ?? p;
const DASHBOARD_VIEW_OPTIONS = POOL_OPTIONS; // dashboard views ≙ the pool list

type Action = {
  id: string;
  label: string;
  toContactStatusId: string | null;
  toContactStatus: { label: string; color: string; pool: StatusPool } | null;
  fromContactStatusIds: string[];
  showOnContactStatusIds: string[];
  contactTypeIds: string[];
  dashboardViewIds: string[];
  visibleToIds: string[];
  global: boolean;
  reminderFormId: string | null;
  reminderFormNote: string | null;
  reminderFormUrl: string | null;
  reminderFormDomain: 'forms' | 'formshub';
  reminderForm: { name: string } | null;
  position: number;
};
type FormRow = { id: string; name: string };
type UserRow = { id: string; email: string; firstName: string; lastName: string };
type TypeRow = { id: string; name: string; active: boolean };

type Draft = {
  label: string;
  dashboardViewIds: string[];
  visibleToIds: string[];
  contactTypeIds: string[];
  showOnContactStatusIds: string[];
  fromContactStatusIds: string[];
  toContactStatusId: string;
  global: boolean;
  reminderFormId: string;
  reminderFormNote: string;
  reminderFormUrl: string;
  reminderFormDomain: 'forms' | 'formshub';
};
const BLANK: Draft = {
  label: '',
  dashboardViewIds: [],
  visibleToIds: [],
  contactTypeIds: [],
  showOnContactStatusIds: [],
  fromContactStatusIds: [],
  toContactStatusId: '',
  global: true,
  reminderFormId: '',
  reminderFormNote: '',
  reminderFormUrl: '',
  reminderFormDomain: 'forms',
};

export function StatusActionsModal({ onClose }: { onClose: () => void }) {
  // Multi-select Contact Types: the Saved list combines every selected pool, and
  // creating fans the action out to each — resolving the status pickers (authored
  // in the first selected pool) into each target pool BY LABEL, skipping any pool
  // that has no status with the chosen New Status name.
  const [pools, setPools] = useState<StatusPool[]>(['patient']);
  const [tab, setTab] = useState<'new' | 'saved'>('new');
  const [actions, setActions] = useState<Action[]>([]);
  const [statusesByPool, setStatusesByPool] = useState<Record<string, ContactStatusRow[]>>({});
  const [forms, setForms] = useState<FormRow[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [types, setTypes] = useState<TypeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(BLANK);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const dragId = useRef<string | null>(null);

  // Lightweight toast (mockup showToast) — used by the Run preview.
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [toastMsg, setToastMsg] = useState('');
  const [toastShow, setToastShow] = useState(false);
  const toast = (m: string) => {
    setToastMsg(m);
    setToastShow(true);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastShow(false), 3600);
  };

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get<Action[]>('/settings/actions').catch(() => [] as Action[]),
      api.get<FormRow[]>('/forms').catch(() => [] as FormRow[]),
      api.get<UserRow[]>('/settings/users').catch(() => [] as UserRow[]),
      api.get<TypeRow[]>('/contact-types').catch(() => [] as TypeRow[]),
    ])
      .then(([a, f, u, t]) => {
        setActions(Array.isArray(a) ? a : []);
        setForms(Array.isArray(f) ? f : []);
        setUsers(Array.isArray(u) ? u : []);
        setTypes(Array.isArray(t) ? t : []);
        setErr(null);
        // Land on Saved when a selected pool already has actions.
        const has = (Array.isArray(a) ? a : []).some(
          (x) => x.toContactStatus?.pool && pools.includes(x.toContactStatus.pool),
        );
        setTab(has ? 'saved' : 'new');
      })
      .catch(() => setErr('Could not load — admin/manager only.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Load every selected pool's rungs on demand (cached thereafter).
  useEffect(() => {
    for (const p of pools) {
      if (statusesByPool[p]) continue;
      api
        .get<ContactStatusRow[]>(`/contact-statuses?pool=${p}`)
        .then((rows) => setStatusesByPool((m) => ({ ...m, [p]: Array.isArray(rows) ? rows : [] })))
        .catch(() => setStatusesByPool((m) => ({ ...m, [p]: [] })));
    }
  }, [pools, statusesByPool]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  const multi = pools.length > 1;
  // The pool whose statuses populate the form's pickers: the edited action's own
  // pool when editing, else the first selected pool (the "authoring" pool).
  const editingAction = editId ? actions.find((a) => a.id === editId) : undefined;
  const formPool: StatusPool =
    (editingAction?.toContactStatus?.pool as StatusPool | undefined) ?? pools[0] ?? 'patient';
  const poolStatuses = statusesByPool[formPool] ?? [];
  const poolActions = actions.filter(
    (a) => a.toContactStatus?.pool && pools.includes(a.toContactStatus.pool),
  );

  // ── option lists ──
  const statusOptions: DdOption[] = poolStatuses.map((s) => ({
    value: s.id,
    label: s.label,
    dot: s.color,
  }));
  const newStatusOptions: DdOption[] = [
    { value: '', label: '— Select New Status —' },
    ...statusOptions,
  ];
  const contactTypeOptions: DdOption[] = [
    { value: CONTACT_TYPE_ALL, label: 'All' },
    ...types.filter((t) => t.active).map((t) => ({ value: t.id, label: t.name })),
  ];
  const visibleToOptions: DdOption[] = [
    ...SETTINGS_ROLES.map((r) => ({ value: `r:${r}`, label: `Role · ${cap(r)}` })),
    ...users.map((u) => ({
      value: `u:${u.id}`,
      label: `${u.firstName} ${u.lastName}`.trim() || u.email,
    })),
  ];
  const formOptions: DdOption[] = [
    { value: '', label: '— No form reminder —' },
    ...forms.map((f) => ({ value: f.id, label: f.name })),
  ];

  // ── label resolvers (saved rows) ──
  // Resolve across every loaded pool — a combined list mixes actions from several.
  const statusLabel = (id: string) => {
    for (const rows of Object.values(statusesByPool)) {
      const hit = rows.find((s) => s.id === id);
      if (hit) return hit.label;
    }
    return '—';
  };
  const visibleToLabel = (ids: string[]) =>
    ids
      .map((v) => visibleToOptions.find((o) => o.value === v)?.label.replace(/^Role · /, '') ?? v)
      .join(', ');

  // ── CRUD ──
  const switchPools = (next: string[]) => {
    const nextPools = next as StatusPool[];
    setPools(nextPools);
    setEditId(null);
    setDraft(BLANK);
    setQ('');
    setTab(
      actions.some((a) => a.toContactStatus?.pool && nextPools.includes(a.toContactStatus.pool))
        ? 'saved'
        : 'new',
    );
  };
  const resetForm = () => {
    setEditId(null);
    setDraft(BLANK);
  };
  const startEdit = (a: Action) => {
    setEditId(a.id);
    setDraft({
      label: a.label,
      dashboardViewIds: a.dashboardViewIds,
      visibleToIds: a.visibleToIds,
      contactTypeIds: a.contactTypeIds,
      showOnContactStatusIds: a.showOnContactStatusIds,
      fromContactStatusIds: a.fromContactStatusIds,
      toContactStatusId: a.toContactStatusId ?? '',
      global: a.global,
      reminderFormId: a.reminderFormId ?? '',
      reminderFormNote: a.reminderFormNote ?? '',
      reminderFormUrl: a.reminderFormUrl ?? '',
      reminderFormDomain: a.reminderFormDomain,
    });
    setTab('new');
  };

  const save = () => {
    const label = draft.label.trim();
    if (!label) return toast('Name the action');
    if (!draft.fromContactStatusIds.length) return toast('Select at least one Current Status');
    if (!draft.showOnContactStatusIds.length) return toast('Select a Show On status');
    if (!draft.toContactStatusId) return toast('Select a New Status');
    if (draft.fromContactStatusIds.every((id) => id === draft.toContactStatusId))
      return toast('Current and New status must differ');
    if (busy) return;
    setBusy(true);
    const common = {
      label,
      dashboardViewIds: draft.dashboardViewIds,
      visibleToIds: draft.visibleToIds,
      contactTypeIds: draft.contactTypeIds,
      global: draft.global,
      reminderFormId: draft.reminderFormId || null,
      reminderFormNote: draft.reminderFormNote || null,
      reminderFormUrl: draft.reminderFormUrl || null,
      reminderFormDomain: draft.reminderFormDomain,
    };

    // Edit → single action, in place (its own pool). Create → fan out to every
    // selected pool, translating the authored status ids into each pool by label.
    if (editId) {
      api
        .patch(`/settings/actions/${editId}`, {
          ...common,
          showOnContactStatusIds: draft.showOnContactStatusIds,
          fromContactStatusIds: draft.fromContactStatusIds,
          toContactStatusId: draft.toContactStatusId,
        })
        .then(() => {
          resetForm();
          setTab('saved');
          load();
          toast('Action updated');
        })
        .catch(() => setErr('Could not save the action.'))
        .finally(() => setBusy(false));
      return;
    }

    const authoring = statusesByPool[formPool] ?? [];
    const labelOf = (id: string) => authoring.find((s) => s.id === id)?.label;
    // Map an authored status id to the same-named rung in a target pool.
    const remap = (ids: string[], rows: ContactStatusRow[]) =>
      ids
        .map((id) => rows.find((s) => s.label === labelOf(id))?.id)
        .filter((v): v is string => !!v);

    const targetPools = pools.length ? pools : [formPool];
    Promise.allSettled(
      targetPools.map((p) => {
        const rows = statusesByPool[p] ?? [];
        const toId =
          p === formPool
            ? draft.toContactStatusId
            : rows.find((s) => s.label === labelOf(draft.toContactStatusId))?.id;
        if (!toId) return Promise.reject(new Error('no-target')); // skip: name missing here
        return api.post('/settings/actions', {
          ...common,
          toContactStatusId: toId,
          fromContactStatusIds:
            p === formPool ? draft.fromContactStatusIds : remap(draft.fromContactStatusIds, rows),
          showOnContactStatusIds:
            p === formPool
              ? draft.showOnContactStatusIds
              : remap(draft.showOnContactStatusIds, rows),
        });
      }),
    )
      .then((results) => {
        const skipped = targetPools.filter((_, i) => results[i]!.status === 'rejected');
        resetForm();
        setTab('saved');
        load();
        if (skipped.length === targetPools.length) {
          setErr(
            `Could not create — no matching “${statusLabel(draft.toContactStatusId)}” status.`,
          );
        } else if (skipped.length) {
          toast(`Saved. Skipped ${skipped.map(poolLabel).join(', ')} — no matching New Status.`);
        } else {
          toast(multi ? `Action saved to ${targetPools.length} contact types` : 'Action saved');
        }
      })
      .catch(() => setErr('Could not save the action.'))
      .finally(() => setBusy(false));
  };

  const remove = async (a: Action) => {
    if (!(await askConfirm(`Delete action “${a.label}”?`))) return;
    api
      .del(`/settings/actions/${a.id}`)
      .then(() => {
        if (editId === a.id) resetForm();
        load();
      })
      .catch(() => setErr('Could not delete.'));
  };

  const firePreview = (a: Action) => {
    const from = a.fromContactStatusIds.length
      ? a.fromContactStatusIds.map(statusLabel).join(', ')
      : 'any status';
    toast(
      `Preview · ${a.label}: moves a contact on ${from} → ${statusLabel(
        a.toContactStatusId ?? '',
      )} (counts up if already there). This fires for real from a contact’s menu.`,
    );
  };

  const reorder = (targetId: string) => {
    const dId = dragId.current;
    dragId.current = null;
    if (!dId || dId === targetId || multi) return; // reorder is single-pool only
    const next = poolActions.slice();
    const di = next.findIndex((a) => a.id === dId);
    const ti = next.findIndex((a) => a.id === targetId);
    if (di < 0 || ti < 0) return;
    const [moved] = next.splice(di, 1);
    next.splice(ti, 0, moved!);
    // Optimistic: re-slot the reordered pool actions back into the full list.
    const ids = new Set(next.map((a) => a.id));
    let k = 0;
    setActions((prev) => prev.map((a) => (ids.has(a.id) ? next[k++]! : a)));
    api.patch('/settings/actions/reorder', { ids: next.map((a) => a.id) }).catch(() => {
      setErr('Could not reorder.');
      load();
    });
  };

  const showFormFields = !!draft.reminderFormId;
  const ql = q.trim().toLowerCase();
  const shownActions = ql
    ? poolActions.filter((a) => a.label.toLowerCase().includes(ql))
    : poolActions;

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-lg" role="dialog" aria-modal="true" aria-label="Status Actions">
        <div className="modal-head">
          <div>
            <h2>Status Actions</h2>
            <p>
              Configure actions that fire a status change. Each action moves a contact from a
              current status to a new one.
            </p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <div style={{ marginBottom: 14 }}>
            <label className="status-mgr-eyebrow">Contact Type</label>
            <MultiDd
              values={pools}
              options={POOL_OPTIONS}
              onChange={switchPools}
              placeholder="— Select contact type(s) —"
              searchable={false}
              selectAll
            />
          </div>

          <div className="status-mgr-tabs">
            <button
              type="button"
              className={`status-mgr-tab${tab === 'new' ? ' active' : ''}`}
              onClick={() => {
                resetForm();
                setTab('new');
              }}
            >
              {editId ? 'Edit Status Action' : 'New Status Action'}
            </button>
            <button
              type="button"
              className={`status-mgr-tab${tab === 'saved' ? ' active' : ''}`}
              onClick={() => setTab('saved')}
            >
              Saved Status Actions
              {poolActions.length > 0 && (
                <span className="status-mgr-tab-badge">{poolActions.length}</span>
              )}
            </button>
          </div>

          {err && <div className="config-err">{err}</div>}

          {tab === 'new' ? (
            <div className="status-mgr-form">
              {multi && !editId && (
                <div className="sa-multi-hint">
                  Status options are shown for <strong>{poolLabel(formPool)}</strong>. On save, this
                  action is created in each selected contact type by matching status names — types
                  without a matching New Status are skipped.
                </div>
              )}
              <div className="status-mgr-form-grid">
                <div className="field field-full">
                  <label>Action name</label>
                  <input
                    type="text"
                    value={draft.label}
                    placeholder="e.g. Change to Contacted"
                    onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
                  />
                </div>
                <div className="field field-full">
                  <label>Dashboard View — which views show this action (empty = all)</label>
                  <MultiDd
                    values={draft.dashboardViewIds}
                    options={DASHBOARD_VIEW_OPTIONS}
                    placeholder="— All dashboard views —"
                    onChange={(v) => setDraft((d) => ({ ...d, dashboardViewIds: v }))}
                  />
                </div>
                <div className="field field-full">
                  <label>
                    Visible to — users or roles who can see and fire this action (empty = all)
                  </label>
                  <MultiDd
                    values={draft.visibleToIds}
                    options={visibleToOptions}
                    placeholder="— All users —"
                    selectAll
                    onChange={(v) => setDraft((d) => ({ ...d, visibleToIds: v }))}
                  />
                </div>
                <div className="field field-full">
                  <label>
                    Contacts this action applies to — select contact types or choose All
                  </label>
                  <MultiDd
                    values={draft.contactTypeIds}
                    options={contactTypeOptions}
                    placeholder="— All contact types —"
                    selectAll
                    onChange={(v) => setDraft((d) => ({ ...d, contactTypeIds: v }))}
                  />
                </div>
                <div className="field field-full">
                  <label>
                    Show action on — statuses where this action appears in the context menu
                  </label>
                  <MultiDd
                    values={draft.showOnContactStatusIds}
                    options={statusOptions}
                    placeholder="— Select statuses —"
                    onChange={(v) => setDraft((d) => ({ ...d, showOnContactStatusIds: v }))}
                  />
                </div>
                <div className="field field-full">
                  <label>Current Status — select one or more</label>
                  <MultiDd
                    values={draft.fromContactStatusIds}
                    options={statusOptions}
                    placeholder="— Select Current Status(es) —"
                    onChange={(v) => setDraft((d) => ({ ...d, fromContactStatusIds: v }))}
                  />
                </div>
                <div className="field field-full">
                  <label>New Status</label>
                  <Dd
                    value={draft.toContactStatusId}
                    options={newStatusOptions}
                    onChange={(v) => setDraft((d) => ({ ...d, toContactStatusId: v }))}
                    searchable={false}
                  />
                </div>
                <div className="field field-full">
                  <label>
                    Form to Fill (Optional) — when this action fires, remind the user to collect a
                    form
                  </label>
                  <Dd
                    value={draft.reminderFormId}
                    options={formOptions}
                    onChange={(v) => setDraft((d) => ({ ...d, reminderFormId: v }))}
                  />
                </div>
                {showFormFields && (
                  <div className="field field-full sa-remind-panel">
                    <div className="field">
                      <label>Reminder note</label>
                      <input
                        type="text"
                        value={draft.reminderFormNote}
                        placeholder="Collect this form before moving on"
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, reminderFormNote: e.target.value }))
                        }
                      />
                    </div>
                    <div className="field">
                      <label>Form URL</label>
                      <input
                        type="url"
                        value={draft.reminderFormUrl}
                        placeholder="https://forms.credifyfast.com/..."
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, reminderFormUrl: e.target.value }))
                        }
                      />
                    </div>
                    <div className="field">
                      <label>Opens at</label>
                      <div className="sa-toggle">
                        {REMINDER_FORM_DOMAINS.map((dom) => (
                          <button
                            key={dom}
                            type="button"
                            className={`sa-toggle-btn${draft.reminderFormDomain === dom ? ' active' : ''}`}
                            onClick={() => setDraft((d) => ({ ...d, reminderFormDomain: dom }))}
                          >
                            {dom}.credifyfast.com
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <div className="field field-full">
                  <label>Scope — how this action counts across statuses</label>
                  <div className="sa-toggle">
                    <button
                      type="button"
                      className={`sa-toggle-btn${draft.global ? ' active' : ''}`}
                      onClick={() => setDraft((d) => ({ ...d, global: true }))}
                    >
                      Global
                    </button>
                    <button
                      type="button"
                      className={`sa-toggle-btn${!draft.global ? ' active' : ''}`}
                      onClick={() => setDraft((d) => ({ ...d, global: false }))}
                    >
                      Unique
                    </button>
                  </div>
                  <p className="sa-scope-hint">
                    {draft.global ? (
                      <>
                        <strong>Global</strong> — count climbs continuously across all statuses
                        (e.g. calls, emails, texts). Moving a contact to a new status does not reset
                        this counter.
                      </>
                    ) : (
                      <>
                        <strong>Unique</strong> — counter is scoped to the target status. Moving to
                        a new status starts the counter fresh at 1 on the next fire.
                      </>
                    )}
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                {editId && (
                  <button className="btn" onClick={resetForm} disabled={busy}>
                    Cancel
                  </button>
                )}
                <button
                  className="btn primary"
                  onClick={save}
                  disabled={busy || (!editId && pools.length === 0)}
                >
                  {editId
                    ? 'Update action'
                    : multi
                      ? `Add to ${pools.length} contact types`
                      : 'Add action'}
                </button>
              </div>
            </div>
          ) : loading ? (
            <p className="muted">Loading…</p>
          ) : poolActions.length === 0 ? (
            <div className="status-mgr-empty">
              No status actions yet. Switch to <strong>New Status Action</strong> to add one.
            </div>
          ) : (
            <>
              <input
                type="text"
                className="sa-search"
                placeholder="Search actions…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <div className="status-mgr-list">
                {shownActions.map((a) => {
                  const showOnText = a.showOnContactStatusIds.length
                    ? a.showOnContactStatusIds.map(statusLabel).join(', ')
                    : 'All statuses';
                  const fromText = a.fromContactStatusIds.map(statusLabel).join(' + ');
                  const vt = a.visibleToIds.length ? visibleToLabel(a.visibleToIds) : '';
                  return (
                    <div
                      className="status-mgr-row sa-row"
                      key={a.id}
                      draggable={!multi}
                      onDragStart={() => !multi && (dragId.current = a.id)}
                      onDragOver={(e) => !multi && e.preventDefault()}
                      onDrop={() => reorder(a.id)}
                    >
                      <span
                        className="status-mgr-handle"
                        title={multi ? 'Select one contact type to reorder' : 'Drag to reorder'}
                        aria-hidden
                        style={multi ? { opacity: 0.3 } : undefined}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <circle cx="9" cy="6" r="1.6" />
                          <circle cx="15" cy="6" r="1.6" />
                          <circle cx="9" cy="12" r="1.6" />
                          <circle cx="15" cy="12" r="1.6" />
                          <circle cx="9" cy="18" r="1.6" />
                          <circle cx="15" cy="18" r="1.6" />
                        </svg>
                      </span>
                      <div className="sa-row-main">
                        <span className="sa-row-name">{a.label}</span>
                        {multi && a.toContactStatus?.pool && (
                          <span className="status-mgr-pool-tag">
                            {poolLabel(a.toContactStatus.pool)}
                          </span>
                        )}
                        {a.global && (
                          <span
                            className="sa-pill-global"
                            title="Global: counter climbs continuously across all statuses and never resets. Unique: counter resets to 1 each time the contact moves to a new status."
                          >
                            Global
                          </span>
                        )}
                        <span className="sa-shows-label">Shows on:</span>
                        <span className="sa-shows-val">{showOnText}</span>
                        {vt && <span className="sa-pill-visible">👁 {vt}</span>}
                        <span className="sa-from">{fromText}</span>
                        <svg
                          className="sa-arrow"
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#1a8a66"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <line x1="5" y1="12" x2="19" y2="12" />
                          <polyline points="12 5 19 12 12 19" />
                        </svg>
                        <span className="sa-to">{statusLabel(a.toContactStatusId ?? '')}</span>
                        {a.reminderFormId && (
                          <span
                            className="sa-pill-form"
                            title={`Form reminder: ${a.reminderForm?.name ?? ''}`}
                          >
                            {a.reminderForm?.name ?? 'Form'}
                          </span>
                        )}
                      </div>
                      <button
                        className="sa-run"
                        onClick={() => firePreview(a)}
                        title="Preview this action"
                      >
                        ▶ Run
                      </button>
                      <button
                        className="status-mgr-btn"
                        title="Edit"
                        aria-label={`Edit ${a.label}`}
                        onClick={() => startEdit(a)}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                        </svg>
                      </button>
                      <button
                        className="status-mgr-btn danger"
                        title="Delete"
                        aria-label={`Delete ${a.label}`}
                        onClick={() => remove(a)}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
      <div className={`app-toast sa-toast${toastShow ? ' show' : ''}`} role="status">
        {toastMsg}
      </div>
    </div>
  );
}
